import React, { Fragment, useContext, useEffect, useState } from "react";
import { StepRenderProps } from "../../../interfaces/FlowInterfaces";
import { LoadInvitationStepValues } from "../../_interfaces/IAdminInvitationStepTypeValues";
import { IAdminInvitationFlowValues } from "../../_interfaces/IAdminInvitationFlowValues";
import LoadingIndicator from "../../../../../components/Shared/LoadingIndicator";
import { Container, createStyles, makeStyles, Paper, Theme } from "@material-ui/core";
import ErrorMessage from "../../../../../components/Shared/ErrorMessage";
import { RootContext } from "../../../../../stores";
import { useParams } from "react-router-dom";
import { AppError } from "../../../../../stores/models/errors/AppError";
import { AppErrors } from "../../../../../stores/models/constants/AppErrors";
import { RegistrationCredentialsForm } from "../../../../authentication/components/RegistrationCredentialsForm";
import { useRegistrationCredentialsController } from "../../hooks/useRegistrationCredentialsController";
import { observer } from "mobx-react";
import { AdminInvitationHeader } from "../AdminInvitationHeader";
import { LogoutRequiredForm } from "../../../../authentication/components/LogoutRequiredForm";
import { useLogoutRequiredController } from "../../hooks/useLogoutRequiredController";
import { ILogoutRequiredFormValues } from "../../../../authentication/_interfaces/ILogoutRequiredFormValues";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        content: {
            marginTop: theme.spacing(2),
            padding: theme.spacing(4),
            display: 'inline-block',
            position: 'relative' // the loading spinner refers to this element
        }
    })
);

const LINK_EXPIRATION_PERIOD = '24 hours';

export const LoadInvitationStep: React.FC<
    StepRenderProps<LoadInvitationStepValues, IAdminInvitationFlowValues>
> = observer(({
    values,
    update,
    submit,
    canSubmit,
    validation,
    allValues
}) => {

    /********* React hooks *********/

    const classes = useStyles();
    const rootStore = useContext(RootContext);
    const userStore = rootStore.userStore;
    const invitationStore = rootStore.invitationStore;
    const { token } = useParams() as { token: string };
    const controller = useRegistrationCredentialsController(values, allValues, update, submit);


    const updateAdapter = async (patch: Partial<ILogoutRequiredFormValues>) => {
        let effectivePatch: Partial<LoadInvitationStepValues> = patch;
        if ('loggedOut' in patch) {
            effectivePatch.logoutRequired = !patch.loggedOut;
        }
        update(patch);
    }

    const logoutRequiredController = useLogoutRequiredController(
        { ...values, prompt: values.logoutRequiredPrompt, loggedOut: !values.logoutRequired },
        allValues,
        updateAdapter,
        submit
    );

    /********* State *********/

    const [getInvitationError, setGetInvitationError] = useState<AppError>();
    const [isLoading, setIsLoading] = useState(true);

    /********* Effects *********/

    useEffect(() => {
        if (values.username === undefined) {
            update({
                username: "",
                password: "",
                confirmedPassword: "",
                token: token,
                currentUserEmail: userStore.user.email,
                currentUserName: userStore.user.name,
                hasExistingOrganizationProfile: userStore.user.hasOrganizationProfile,
                logoutRequiredPrompt: "This invitation was sent to a different email address. Please logout to continue or ask an organization administrator to send a new invitation to the email above."
            });
            getInvitation();
        } else {
            update({
                password: "",
                confirmedPassword: "",
            });
            setIsLoading(false);
        }
    }, [])

    /********* API Requests *********/

    const getInvitation = async () => {
        try {
            if (token) {
                setIsLoading(true);
                const response = await invitationStore.getAdminInvite(token);
                if (response instanceof AppError) {
                    setGetInvitationError(response);
                } else {
                    update({
                        username: response.email,
                        fieldProperties: { username: { disabled: true } },
                        organizationName: response.organization.name,
                        logoutRequired: response.logoutRequired,
                        inviteForCurrentUser: response.inviteForCurrentUser
                    });
                }
                setIsLoading(false);
            }
        } catch (err) {
            throw err;
        } finally {
            setIsLoading(false);
        }
    }

    /********* Event handlers *********/

    const onRequestNewInvitationClicked = async () => {
        update({ requestNewLink: true });
        submit({ requestNewLink: true });
    }

    const onErrorActionButtonClicked = async () => {
        switch (getInvitationError?.code) {
            case AppErrors.TOKEN_EXPIRED.code:
                return onRequestNewInvitationClicked();
            case AppErrors.TOKEN_USED.code:
            case AppErrors.NOT_FOUND.code:
            case AppErrors.TOKEN_REVOKED.code:
            default:
                break;
        }
    }

    /********* Helper Methods *********/

    const getErrorTitleText = () => {
        switch (getInvitationError?.code) {
            case AppErrors.NOT_FOUND.code:
                return 'Oops! This link is invalid.';
            case AppErrors.TOKEN_USED.code:
            case AppErrors.TOKEN_EXPIRED.code:
            case AppErrors.TOKEN_REVOKED.code:
                return 'Oops! This link is no longer valid.';
            default:
                return "";
        }
    }

    const getErrorDetailsText = () => {
        switch (getInvitationError?.code) {
            case AppErrors.NOT_FOUND.code:
                return "Please double check that you've entered the link exactly as it appears in your email.";
            case AppErrors.TOKEN_USED.code:
                return "The invitation has already been accepted.";
            case AppErrors.TOKEN_EXPIRED.code:
                return `For security purposes, links expire after ${LINK_EXPIRATION_PERIOD}. Please request a new link to continue.`;
            case AppErrors.TOKEN_REVOKED.code:
                return "The organization that sent this invitation has since revoked it. Please contact an administrator at the organization if you believe this was in error."
            default:
                return "";
        }
    }

    const getErrorActionButtonText = () => {
        switch (getInvitationError?.code) {
            case AppErrors.TOKEN_EXPIRED.code:
                return "Request New Link";
            case AppErrors.TOKEN_USED.code:
            case AppErrors.NOT_FOUND.code:
            case AppErrors.TOKEN_REVOKED.code:
            default:
                break;
        }
    }

    const errorActionButtonText = getErrorActionButtonText();

    /********* Render *********/

    return (
        <Fragment>
            {isLoading || allValues.inviteForCurrentUser === true
                ? <LoadingIndicator />
                : <Container>
                    <Paper className={classes.content}>
                        {values.organizationName
                            ? values.logoutRequired
                                ? <LogoutRequiredForm
                                    values={logoutRequiredController.values}
                                    onChange={(patch) => {
                                        logoutRequiredController.onChange(patch);
                                        update(patch); // sync with Flow
                                    }}
                                    onSubmit={logoutRequiredController.onSubmit}
                                    isSubmitting={logoutRequiredController.isSubmitting}
                                    error={logoutRequiredController.error}
                                    fieldErrors={logoutRequiredController.fieldErrors}
                                />
                                : <Fragment>
                                    <AdminInvitationHeader organizationName={values.organizationName} />
                                    <RegistrationCredentialsForm
                                        values={controller.values}
                                        onChange={(patch) => {
                                            controller.onChange(patch);
                                            update(patch); // sync with Flow
                                        }}
                                        onSubmit={controller.onSubmit}
                                        isSubmitting={controller.isSubmitting}
                                        error={controller.error}
                                        fieldErrors={controller.fieldErrors}
                                    />
                                </Fragment>
                            : <ErrorMessage
                                title={getErrorTitleText()}
                                details={getErrorDetailsText()}
                                ButtonProps={
                                    errorActionButtonText
                                        ? {
                                            children: errorActionButtonText,
                                            onClick: onErrorActionButtonClicked,
                                            disabled: !canSubmit
                                        }
                                        : undefined
                                }
                            />
                        }
                    </Paper>
                </Container>
            }
        </Fragment>
    );
});
